import React from 'react';
import { useLogin } from '../../hooks/useLogin';
import { Button, Typography, Iconography } from '@lucid/lego';
import styles from './Login.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TypedIconography = Iconography as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TypedTypography = Typography as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TypedButton = Button as any;

export interface LoginProps {
  /**
   * Title to display on the login page
   * @default "Partner Portal"
   */
  title?: string;
  /**
   * Subtitle to display when user is not logged out
   * @default "Sign in to access your account"
   */
  subtitle?: string;
  /**
   * Message to display when user has just logged out
   * @default "You have successfully logged out. Please use the login button below to login."
   */
  logoutMessage?: string;
  /**
   * Message to display when session has expired
   * @default "Your session has expired. Please log in again to continue."
   */
  sessionExpiredMessage?: string;
  /**
   * Message to display when authentication error occurs
   * @default "Unable to verify your account. Please log in again."
   */
  authErrorMessage?: string;
  /**
   * Label for the login button
   * @default "Login"
   */
  loginButtonLabel?: string;
  /**
   * Whether to show the logo section
   * @default true
   */
  showLogo?: boolean;
  /**
   * Custom logo component to render instead of default
   */
  customLogo?: React.ReactNode;
  /**
   * Google Analytics component name
   * @default "login-page"
   */
  gaComponent?: string;
}

const Login: React.FC<LoginProps> = ({
  title = 'Partner Portal',
  subtitle = 'Sign in to access your account',
  logoutMessage = 'You have successfully logged out. Please use the login button below to login.',
  sessionExpiredMessage = 'Your session has expired. Please log in again to continue.',
  authErrorMessage = 'Unable to verify your account. Please log in again.',
  loginButtonLabel = 'Login',
  showLogo = true,
  customLogo,
  gaComponent = 'login-page',
}) => {
  const { handleLogin, isLoggedOut, isSessionExpired, isAuthError } = useLogin();

  // Determine which message to show
  const getMessage = () => {
    if (isAuthError) return authErrorMessage;
    if (isSessionExpired) return sessionExpiredMessage;
    if (isLoggedOut) return logoutMessage;
    return subtitle;
  };

  return (
    <div className={styles.login}>
      <div className={styles.container}>
        <div className={styles.loginBox}>
          {showLogo && (
            <div className={styles.logo}>
              {customLogo || (
                // eslint-disable-next-line react/forbid-dom-props
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TypedIconography category="brand" name="logo" color="black" size={32} />
                  <TypedIconography category="brand" name="bear" color="black" size={32} />
                </div>
              )}
            </div>
          )}
          <div className={styles.title}>
            <TypedTypography variant="headline" size="medium" color="black" align="center">
              {title}
            </TypedTypography>
          </div>

          <div className={styles.subtitle}>
            <TypedTypography variant="body" size="medium" color="gray-8" align="center">
              {getMessage()}
            </TypedTypography>
          </div>
          <TypedButton
            type="primary"
            color="neutral"
            label={loginButtonLabel}
            isFluid
            gaComponent={gaComponent}
            dataTestId="login-button"
            handleClick={handleLogin}
            theme="light"
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
